/**
 * UserProfileService - Manages user profiles and founding member status
 */

import { authFetch } from './authFetch.js';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

class UserProfileService {
    constructor() {
        this.profile = null;
        this.cacheKey = 'zephyr_user_profile';
        this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * Get user profile from API or cache
     */
    async getProfile(userId) {
        if (!userId) {
            console.warn('ProfileService: No userId provided');
            return null;
        }

        // Check cache first
        const cached = this.getCachedProfile(userId);
        if (cached) {
            console.log('ProfileService: Using cached profile');
            this.profile = cached;
            return cached;
        }

        try {
            console.log('ProfileService: Fetching profile from API');
            const response = await authFetch(`${API_URL}/api/profile?userId=${userId}`);
            
            if (response.status === 404) {
                console.log('ProfileService: Profile not found (user may not have subscribed yet)');
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch profile: ${response.status}`);
            }

            const profile = await response.json();
            this.profile = profile;
            this.cacheProfile(userId, profile);
            console.log('ProfileService: Profile loaded', profile);
            return profile;

        } catch (error) {
            console.error('ProfileService: Error fetching profile:', error);
            return null;
        }
    }

    /**
     * Update user profile (displayName and sharePreferences only)
     */
    async updateProfile(userId, updates) {
        if (!userId) {
            throw new Error('userId is required');
        }

        try {
            const response = await authFetch(`${API_URL}/api/profile`, {
                method: 'PUT',
                body: JSON.stringify({ userId, ...updates })
            });

            if (!response.ok) {
                throw new Error(`Failed to update profile: ${response.status}`);
            }

            const updatedProfile = await response.json();
            this.profile = updatedProfile;
            this.cacheProfile(userId, updatedProfile);
            console.log('ProfileService: Profile updated', updatedProfile);
            return updatedProfile;

        } catch (error) {
            console.error('ProfileService: Error updating profile:', error);
            throw error;
        }
    }

    /**
     * Get public profile for another user
     */
    async getPublicProfile(userId) {
        try {
            const response = await fetch(`${API_URL}/api/profile/${userId}/public`);
            
            if (response.status === 403) {
                console.log('ProfileService: Profile is private');
                return null;
            }

            if (response.status === 404) {
                console.log('ProfileService: Profile not found');
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch public profile: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('ProfileService: Error fetching public profile:', error);
            return null;
        }
    }

    /**
     * Check if user is a founding member
     */
    isFoundingMember() {
        return this.profile?.isFoundingMember === true;
    }

    /**
     * Get founding member number (1-100)
     */
    getFoundingMemberNumber() {
        return this.profile?.foundingMemberNumber || null;
    }

    /**
     * Get display name
     */
    getDisplayName() {
        return this.profile?.displayName || 'User';
    }

    /**
     * Clear profile cache
     */
    clearCache() {
        localStorage.removeItem(this.cacheKey);
        this.profile = null;
    }

    /**
     * Cache profile in localStorage
     */
    cacheProfile(userId, profile) {
        const cacheData = {
            userId,
            profile,
            timestamp: Date.now()
        };
        localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    }

    /**
     * Get cached profile if valid
     */
    getCachedProfile(userId) {
        try {
            const cached = localStorage.getItem(this.cacheKey);
            if (!cached) return null;

            const { userId: cachedUserId, profile, timestamp } = JSON.parse(cached);
            
            // Check if cache is for the same user and not expired
            if (cachedUserId === userId && Date.now() - timestamp < this.cacheExpiry) {
                return profile;
            }

            return null;
        } catch (error) {
            console.error('ProfileService: Error reading cache:', error);
            return null;
        }
    }

    /**
     * Get current profile (from memory)
     */
    getCurrentProfile() {
        return this.profile;
    }
}

// Export singleton instance
export const userProfileService = new UserProfileService();
